import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form } from "@themesberg/react-bootstrap";

import geoNow from "../utils/geonow";

export const ProjectDescriptionImageEditor = ({
    projectId,
    existingImageName,
    canEdit,
    onImageChanged
}) => {
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedPreviewUrl, setSelectedPreviewUrl] = useState(null);
    const [currentImageUrl, setCurrentImageUrl] = useState(null);
    const [storedImageName, setStoredImageName] = useState(existingImageName ?? null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null);

    useEffect(() => {
        setFeedback(null);
    }, [projectId]);

    useEffect(() => {
        let cancelled = false;

        setSelectedFile(null);
        setSelectedPreviewUrl(null);
        setStoredImageName(existingImageName ?? null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        if (!projectId || !existingImageName) {
            setCurrentImageUrl(null);
            setIsLoadingPreview(false);
            return () => {
                cancelled = true;
            };
        }

        setIsLoadingPreview(true);
        geoNow.api.project.getDescriptionImageUrl(geoNow.getToken(), projectId, error => {
            if (!cancelled) {
                setFeedback({
                    variant: "warning",
                    message: `目前圖片無法載入：${error.message}`
                });
            }
        })
            .then(imageUrl => {
                if (!cancelled) {
                    setCurrentImageUrl(imageUrl ?? null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoadingPreview(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [projectId, existingImageName]);

    const handleFileSelected = event => {
        const file = event.target.files?.[0] ?? null;
        setFeedback(null);
        setSelectedFile(file);
        setSelectedPreviewUrl(null);

        if (!file) {
            return;
        }
        if (!file.type?.startsWith("image/")) {
            setSelectedFile(null);
            event.target.value = "";
            setFeedback({ variant: "danger", message: "請選擇圖片檔案。" });
            return;
        }

        const reader = new FileReader();
        reader.onload = () => setSelectedPreviewUrl(reader.result);
        reader.onerror = () => {
            setSelectedFile(null);
            setFeedback({ variant: "danger", message: "無法讀取選擇的圖片。" });
        };
        reader.readAsDataURL(file);
    };

    const uploadImage = async () => {
        if (!selectedFile || !projectId || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setFeedback(null);
        try {
            const result = await geoNow.api.project.uploadDescriptionImage(
                geoNow.getToken(),
                projectId,
                selectedFile
            );
            if (!result || result.status !== 1) {
                throw new Error(result?.message ?? "圖片上傳失敗。");
            }

            setCurrentImageUrl(selectedPreviewUrl);
            setStoredImageName(result.message);
            setSelectedFile(null);
            setSelectedPreviewUrl(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setFeedback({ variant: "success", message: "首頁圖片已上傳並立即生效。" });
            onImageChanged?.(result.message);
        } catch (error) {
            setFeedback({ variant: "danger", message: error.message ?? "圖片上傳失敗。" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const removeImage = async () => {
        if (!storedImageName || !projectId || isSubmitting) {
            return;
        }
        if (!window.confirm("確定要移除首頁圖片設定嗎？後端既有圖片檔案會保留。")) {
            return;
        }

        setIsSubmitting(true);
        setFeedback(null);
        try {
            const result = await geoNow.api.project.clearDescriptionImage(
                geoNow.getToken(),
                projectId
            );
            if (!result || result.status !== 1) {
                throw new Error(result?.message ?? "移除圖片失敗。");
            }

            setCurrentImageUrl(null);
            setStoredImageName(null);
            setSelectedFile(null);
            setSelectedPreviewUrl(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setFeedback({
                variant: "success",
                message: "首頁圖片設定已移除，後端圖片檔案仍保留。"
            });
            onImageChanged?.(null);
        } catch (error) {
            setFeedback({ variant: "danger", message: error.message ?? "移除圖片失敗。" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const previewUrl = selectedPreviewUrl ?? currentImageUrl;

    return (
        <>
            {isLoadingPreview && <div className="text-muted mb-2">載入目前圖片中...</div>}
            {previewUrl && (
                <div className="mb-3">
                    <div className="text-muted mb-2">
                        {selectedPreviewUrl ? "待上傳圖片預覽" : "目前首頁圖片"}
                    </div>
                    <img
                        src={previewUrl}
                        alt={selectedPreviewUrl ? "待上傳首頁圖片預覽" : "目前首頁圖片"}
                        style={{
                            display: "block",
                            maxWidth: "100%",
                            maxHeight: "360px",
                            objectFit: "contain",
                            border: "1px solid #d1d7e0",
                            borderRadius: "6px"
                        }}
                    />
                </div>
            )}

            <Form.Control
                ref={fileInputRef}
                type="file"
                accept=".webp,.png,.gif,.bmp,.jpg,.jpeg,image/webp,image/png,image/gif,image/bmp,image/jpeg"
                onChange={handleFileSelected}
                disabled={!canEdit || isSubmitting}
            />
            <div className="mt-2 d-flex flex-wrap gap-2">
                <Button
                    type="button"
                    variant="primary"
                    onClick={uploadImage}
                    disabled={!canEdit || !selectedFile || !selectedPreviewUrl || isSubmitting}
                >
                    {isSubmitting ? "處理中..." : "上傳圖片"}
                </Button>
                <Button
                    type="button"
                    variant="danger"
                    onClick={removeImage}
                    disabled={!canEdit || !storedImageName || isSubmitting}
                >
                    移除圖片
                </Button>
            </div>

            <Form.Text className="text-muted">
                選擇圖片後會先顯示預覽，按「上傳圖片」才會生效。移除設定不會刪除後端實體檔案。
            </Form.Text>
            {feedback && (
                <Alert variant={feedback.variant} className="mt-2 mb-0">
                    {feedback.message}
                </Alert>
            )}
        </>
    );
};
